import { useState } from "react"
import { Layout, Volume2, VolumeX } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { ImageWithFallback } from "@/components/common/ImageWithFallback"
import { localizeName, localizeDescription, localizeTextPrompt } from "@/lib/i18n-content"
import type { Template } from "@/hooks/useTemplates"
import {
  parseTemplateParameters,
  resolveTemplate,
  extractDefaultValues,
} from "@/lib/template-parameters"

/** 썸네일 URL에 캐시 버스팅 파라미터 추가 */
function withCacheBusting(url: string | null, updatedAt: string): string | null {
  if (!url) return null
  const timestamp = new Date(updatedAt).getTime()
  return `${url}?v=${timestamp}`
}

/** 템플릿 프리뷰 모달 */
export function TemplatePreviewModal({
  template,
  isOpen,
  onClose,
  onUse,
}: {
  template: Template | null
  isOpen: boolean
  onClose: () => void
  onUse?: () => void
}) {
  const { t } = useTranslation()
  const [isMuted, setIsMuted] = useState(true)
  const [prevResetKey, setPrevResetKey] = useState('')
  const resetKey = `${isOpen ? 1 : 0}-${template?.id ?? ''}`
  if (isOpen && resetKey !== prevResetKey) {
    setPrevResetKey(resetKey)
    setIsMuted(true)
  }

  if (!template) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full h-full max-w-none max-h-none sm:max-w-4xl sm:max-h-[90vh] sm:h-auto p-0 overflow-y-auto overscroll-contain rounded-none sm:rounded-lg inset-0 sm:inset-auto sm:top-[50%] sm:left-[50%] translate-x-0 translate-y-0 sm:translate-x-[-50%] sm:translate-y-[-50%]">
        <DialogTitle className="sr-only">{localizeName(template)}</DialogTitle>
        <div className="flex flex-col sm:flex-row min-h-0">
          {/* 이미지 영역 */}
          <div className="flex-shrink-0 sm:flex-1 bg-slate-100 flex items-center justify-center p-4">
            {template.videoUrl ? (
              <div className="relative">
                <video
                  src={withCacheBusting(template.videoUrl, template.updatedAt) ?? undefined}
                  poster={withCacheBusting(template.thumbnailUrl, template.updatedAt) ?? undefined}
                  autoPlay
                  muted={isMuted}
                  loop
                  playsInline
                  preload="metadata"
                  className="w-auto max-w-full h-auto max-h-[640px] object-contain rounded-lg shadow-lg mx-auto"
                />
                <button
                  type="button"
                  onClick={() => setIsMuted((prev) => !prev)}
                  aria-label={isMuted ? t('templates.soundOn') : t('templates.soundOff')}
                  title={isMuted ? t('templates.soundOn') : t('templates.soundOff')}
                  className="absolute right-2 bottom-2 inline-flex items-center justify-center rounded-full bg-black/70 p-2 text-white hover:bg-black/80"
                >
                  {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                </button>
              </div>
            ) : template.thumbnailUrl ? (
              <ImageWithFallback
                src={withCacheBusting(template.thumbnailUrl, template.updatedAt) ?? ''}
                alt={localizeName(template)}
                className="w-auto max-w-full h-auto max-h-[640px] object-contain rounded-lg shadow-lg mx-auto"
                fallbackClassName="w-32 h-32"
              />
            ) : (
              <div className="w-32 h-32 flex items-center justify-center">
                <Layout className="w-16 h-16 text-slate-300" />
              </div>
            )}
          </div>

          {/* 정보 영역 */}
          <div className="flex-shrink-0 w-full sm:w-80 p-6 flex flex-col bg-white">
            <h2 className="text-xl font-semibold text-slate-900 mb-2 pr-8">
              {localizeName(template)}
            </h2>

            <p className="text-sm text-slate-600 mb-4">
              {localizeDescription(template)}
            </p>

            {/* 프롬프트 (현재 언어로 현지화 후, 파라미터는 기본값으로 치환하여 표시) */}
            {template.textPrompt && (
              <div className="mb-4 p-3 bg-slate-50 rounded-lg max-h-[300px] overflow-y-auto">
                <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {(() => {
                    const localizedPrompt = localizeTextPrompt(template)
                    return resolveTemplate(
                      localizedPrompt,
                      extractDefaultValues(parseTemplateParameters(localizedPrompt))
                    )
                  })()}
                </p>
              </div>
            )}

            {/* 비디오 설정 정보 */}
            <div className="mb-4 space-y-1">
              {template.ratio && (
                <div className="text-sm text-slate-500">
                  {t('templates.ratio', { ratio: template.ratio })}
                </div>
              )}
              {template.resolution && (
                <div className="text-sm text-slate-500">
                  {t('templates.resolution', { resolution: template.resolution })}
                </div>
              )}
              {template.duration && (
                <div className="text-sm text-slate-500">
                  {t('templates.duration', { count: template.duration })}
                </div>
              )}
            </div>

            {/* 사용하기 버튼 (onUse가 있을 때만 표시) */}
            {onUse && (
              <button
                onClick={() => {
                  onUse()
                  onClose()
                }}
                className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                {t('templates.useThisTemplate')}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
